import React, { useState, useEffect } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { CompanyLogo } from './Logo'
import { useAuth } from '../lib/AuthContext'
import { onTabReturn } from '../lib/onTabReturn'

// Grouped sidebar. Group headings are non-clickable labels only. Items flagged adminQM show only to
// Admin/QM (canApprove) — unchanged visibility, just regrouped. (Questionnaires kept under Operations.)
const NAV_GROUPS = [
  { group: 'Operations', items: [
    { to: '/', icon: '⊞', label: 'Dashboard', exact: true },
    { to: '/clients', icon: '👤', label: 'Clients' },
    { to: '/jobs', icon: '📋', label: 'Jobs' },
    { to: '/my-audits', icon: '🗒', label: 'My Audits', myAudits: true },
    { to: '/pipeline', icon: '📊', label: 'Tracker' },
    { to: '/prospects', icon: '🎯', label: 'Prospects', quote: true },
    { to: '/quotations', icon: '📄', label: 'Quotations' },
    { to: '/questionnaires', icon: '📝', label: 'Questionnaires', adminQM: true },
  ] },
  { group: 'Management System', items: [
    { to: '/auditors', icon: '✓', label: 'Personnel' },
    { to: '/controlled-documents', icon: '📁', label: 'Controlled Documents' },
    { to: '/impartiality', icon: '⚖', label: 'Impartiality Register' },
    { to: '/complaints', icon: '📨', label: 'Complaints' },
    { to: '/internal-audit', icon: '🔍', label: 'CCIS Internal Audit', adminQM: true },
    { to: '/management-review', icon: '🧭', label: 'Management Review', adminQM: true },
    { to: '/records-retention', icon: '🗄', label: 'Records Retention', adminQM: true },
  ] },
  { group: 'Administration', items: [
    { to: '/assessment-access', icon: '🔑', label: 'Assessment Access', adminQM: true },
    { to: '/settings', icon: '⚙', label: 'Settings', admin: true },
    { to: '/account', icon: '🔐', label: 'My Account' },
    { to: '/audit-trail', icon: '📋', label: 'Audit trail' },
  ] },
]

// Pure Auditor accounts (role = 'Auditor') see only their own audits + read-only Controlled Documents.
// Dual-hat internal users (QM/Ops who are also auditors) keep their full internal nav.
const AUDITOR_GROUPS = [
  { group: 'Auditor', items: [
    { to: '/', icon: '🗒', label: 'My Audits', exact: true },
    { to: '/complaints', icon: '📨', label: 'My Findings' },
    { to: '/controlled-documents', icon: '📁', label: 'Controlled Documents' },
    { to: '/account', icon: '🔐', label: 'My Account' },
  ] },
]

export default function Layout({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { profile, role, isAuditor, isAdmin, canApprove, canCreateQuote, showMyAudits, loading } = useAuth()
  // Mobile nav drawer (phones only — CSS hides the toggle on desktop). Closes on any navigation.
  const [navOpen, setNavOpen] = useState(false)
  useEffect(() => { setNavOpen(false) }, [location.pathname])
  // Collapsed sidebar: auditors work on the checklist all day and need the page width. The choice is
  // remembered per browser, so it survives navigation and the next sign-in. Desktop/tablet only — below
  // 600px the sidebar is already an off-canvas drawer.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('auditpm_nav_collapsed') === '1' } catch (e) { return false }
  })
  const toggleNav = () => setCollapsed(v => {
    const next = !v
    try { localStorage.setItem('auditpm_nav_collapsed', next ? '1' : '0') } catch (e) {}
    return next
  })
  // Grouped nav. Internal Audit / Management Review / Records Retention stay Admin/QM-only (canApprove).
  const navGroups = isAuditor ? AUDITOR_GROUPS : NAV_GROUPS
  // Badge on "Quotations" = Draft quotes waiting to be prepared & sent. Refetched on each navigation and
  // when the tab regains focus, so it drops once a quote is prepared+sent (and rises on new submissions).
  const [draftCount, setDraftCount] = useState(0)
  useEffect(() => {
    if (isAuditor) return
    let alive = true
    async function loadCount() {
      const { count } = await supabase.from('quotations').select('id', { count: 'exact', head: true }).eq('status', 'Draft')
      if (alive) setDraftCount(count || 0)
    }
    loadCount()
    const stop = onTabReturn(loadCount)   // once per return to the tab (not twice)
    return () => { alive = false; stop() }
  }, [isAuditor, location.pathname])
  async function handleLogout() {
    await supabase.auth.signOut()
    navigate('/login')
  }

  // Least privilege while auth/profile/role is still resolving: render a neutral shell with NO navigation
  // and NO page content — so an Auditor (or anyone) never briefly sees the full internal sidebar on refresh.
  if (loading) {
    return (
      <div className={`layout ${collapsed ? 'nav-collapsed' : ''}`}>
        <aside className="sidebar">
          <div className="sidebar-logo">
            <CompanyLogo size="sm" style={{ filter: 'brightness(0) invert(1)', marginBottom: 4, maxWidth: 140 }} />
            <span>AuditPM</span>
          </div>
        </aside>
        <main className="main-content">
          <div style={{ color: 'var(--text3)', padding: 20 }}>Loading…</div>
        </main>
      </div>
    )
  }

  return (
    <div className={`layout ${collapsed ? 'nav-collapsed' : ''}`}>
      <div className="mobile-topbar">
        <button className="nav-toggle" onClick={() => setNavOpen(o => !o)} aria-label="Menu">☰</button>
        <span className="brand">CCIS · AuditPM</span>
      </div>
      {navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} />}
      <aside className={`sidebar ${navOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <CompanyLogo size="sm" style={{ filter: 'brightness(0) invert(1)', marginBottom: 4, maxWidth: 140 }} />
          <span>AuditPM</span>
        </div>
        <button className="nav-collapse-btn" onClick={toggleNav}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'} title={collapsed ? 'Expand menu' : 'Collapse menu'}>
          <span className="nav-collapse-icon">{collapsed ? '»' : '«'}</span>
          <span className="nav-label">Collapse menu</span>
        </button>
        <nav>
          {navGroups.map((g, gi) => {
            const items = g.items.filter(n =>
              (!n.adminQM || canApprove) && (!n.quote || canCreateQuote) && (!n.admin || isAdmin) && (!n.myAudits || showMyAudits))
            if (!items.length) return null
            return (
              <div key={g.group}>
                <div className="nav-group-title" style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.38)', padding: gi === 0 ? '4px 16px 4px' : '16px 16px 4px' }}>{g.group}</div>
                {items.map(n => (
                  <NavLink key={n.to} to={n.to} end={n.exact} title={collapsed ? n.label : undefined}
                    className={({ isActive }) => isActive ? 'active' : ''}>
                    <span style={{ fontSize: 14, width: 18, textAlign: 'center', flexShrink: 0 }}>{n.icon}</span>
                    <span className="nav-label">{n.label}</span>
                    {n.to === '/quotations' && draftCount > 0 && (
                      <span className="nav-badge" title={`${draftCount} quotation${draftCount > 1 ? 's' : ''} to prepare & send`}
                        style={{ marginLeft: 'auto', background: '#E6A817', color: '#fff', fontSize: 13, fontWeight: 600, borderRadius: 10, padding: '0 7px', minWidth: 18, height: 18, lineHeight: '18px', textAlign: 'center', flexShrink: 0 }}>{draftCount}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>
        <div className="sidebar-footer">
          {profile && (
            <div className="nav-who" style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 6, lineHeight: 1.4 }}>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>{profile.name}</div>
              <div>{role}</div>
            </div>
          )}
          <button onClick={handleLogout}>Sign out</button>
        </div>
      </aside>
      <main className="main-content">
        {children}
      </main>
    </div>
  )
}
